import React, { FC, Fragment, useCallback, useState } from "react";
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  PopoverOrigin,
  Tooltip,
  Typography,
} from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { SendReportDialog } from "./components/dialog/SendReportDialog";
import SendSvgIcon from "../../../../assets/icons/app-related/SendSvgIcon";
import { getLabelByBillingSourceType } from "../../../utils/helpers/getLabelByBillingSourceType";
import { ReportSourceType } from "@/services/cloudchipr.api";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { OneTimeReportType } from "@/components/pages/common/send-report/utils/types/types";
import { SendReportTypeMenuItem } from "@/components/pages/common/send-report/components/SendReportTypeMenuItem";

export interface SendReportActionItemProps {
  sourceId: string;
  onClose?(): void;
  sourceName: string;
  disabled?: boolean;
  iconButton?: boolean;
  iconSx?: SxProps<Theme>;
  menuItemSx?: SxProps<Theme>;
  sourceType: ReportSourceType;
  disabledTooltipText?: string;
}

export const SendReportActionItem: FC<SendReportActionItemProps> = ({
  iconSx,
  onClose,
  disabled,
  sourceId,
  menuItemSx,
  iconButton,
  sourceName,
  sourceType,
  disabledTooltipText,
}) => {
  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const [type, setType] = useState<OneTimeReportType | undefined>();

  const closeHandler = useCallback(() => {
    onClose?.();
    setType(undefined);
  }, [onClose]);

  const menuItemClickHandler = useCallback(
    (type: OneTimeReportType) => {
      setType(type);
      closeMenu();
    },
    [closeMenu],
  );

  return (
    <Fragment>
      {iconButton ? (
        <Tooltip
          arrow
          placement="top"
          title={
            disabledTooltipText ??
            `Send ${getLabelByBillingSourceType(sourceType, true)}`
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={openMenu}
              disabled={disabled}
              sx={{
                backgroundColor: open ? "grey.200" : undefined,
              }}
            >
              <SendSvgIcon color={!disabled ? "primary" : undefined} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <MenuItem onClick={openMenu} disabled={disabled} sx={menuItemSx}>
          <ListItemIcon sx={iconSx}>
            <SendSvgIcon />
          </ListItemIcon>
          <Typography variant="body2">Send</Typography>
        </MenuItem>
      )}

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={closeMenu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <SendReportTypeMenuItem onClick={menuItemClickHandler} type="slack" />
        <SendReportTypeMenuItem onClick={menuItemClickHandler} type="email" />
      </Menu>

      {type && (
        <SendReportDialog
          type={type}
          sourceId={sourceId}
          onClose={closeHandler}
          sourceName={sourceName}
          sourceType={sourceType}
        />
      )}
    </Fragment>
  );
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};
